import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Download,
  Youtube,
  Zap,
  Target,
  Hash,
  Tag as TagIcon,
  Type,
  Image as ImageIcon,
  Clock,
  Sparkles,
  TrendingUp,
  LayoutTemplate,
  FileText
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../../../firebase.init";



const YoutubeTitleTagsGenerator = () => {
  // --- AUTH & CORE STATE (UNCHANGED) ---
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [loadingTools, setLoadingTools] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // --- FORM STATE ---
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("General Audience");
  const [contentType, setContentType] = useState("Explainer");
  const [tone, setTone] = useState("Curiosity-Driven");
  const [lengthPref, setLengthPref] = useState("Medium (50–65 characters)");
  
  // Toggles
  const [includePowerWords, setIncludePowerWords] = useState(true);
  const [includeClickbait, setIncludeClickbait] = useState(false);
  const [includeDescription, setIncludeDescription] = useState(true); // New Description Toggle
  const [includeSeoTags, setIncludeSeoTags] = useState(true);
  const [includeHashtags, setIncludeHashtags] = useState(true);

  // --- OUTPUT STATE ---
  const [resultData, setResultData] = useState(null);

  // --- CREDIT & TOOL CONFIG (UNCHANGED) ---
  const [credits, setCredits] = useState(0);
  const [creditCost, setCreditCost] = useState(2);
  const [apiKeys, setApiKeys] = useState([]);

  // --- OPTIONS ---
  const audienceOptions = [
    "General Audience",
    "Beginners / Novices",
    "Intermediate Learners",
    "Experts / Professionals",
    "Students",
    "Teenagers",
    "Parents",
    "Tech Enthusiasts",
    "Gamers",
    "Entrepreneurs / Business Owners",
    "Investors",
    "Health & Fitness Enthusiasts",
    "Travelers",
    "Foodies",
    "DIYers / Makers",
    "Artists / Creatives"
  ];
  
  const contentTypeOptions = ["Tutorial", "Review", "Comparison", "List", "Explainer", "Trending"];
  const toneOptions = ["Professional", "Energetic", "Educational", "Bold", "Curiosity-Driven"];
  const lengthOptions = ["Short (Under 50 chars)", "Medium (50–65 chars)", "Long (65–75 chars)"];

  // Fetch API keys (Unchanged)
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0) setError("No API keys available. Please contact administrator.");
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Fetch Tools List (Unchanged)
  const fetchToolsList = async () => {
    try {
      const response = await axios.get(`https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/tools-section`);
      if (response.data && response.data.success && response.data.data && response.data.data.tools) {
        const tool = response.data.data.tools.find(t => t.id === 5); 
        if (tool) {
            setCreditCost(tool.creditCost);
        }
      }
    } catch (err) {
        console.warn("Could not fetch tools list, using default credit cost");
    } finally {
        setLoadingTools(false);
    }
  };

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // Initial Data Fetch (Unchanged)
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        setLoadingTools(false);
        return;
      }
      try {
        await Promise.all([fetchApiKeys(), fetchToolsList()]);
        
        const userResponse = await axios.get(`https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
          setCredits(userData.currentBalance);
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Prompt Engineering for Titles, Tags, Desc & Hashtags
  const constructPrompt = () => {
    return `You are a master YouTube strategist and CTR optimization expert. 
    Generate a highly optimized metadata package for a YouTube video based on these parameters:

    - Video Topic: ${topic}
    - Target Audience: ${audience}
    - Content Type: ${contentType}
    - Tone: ${tone}
    - Target Title Length: ${lengthPref}
    - Use Power Words: ${includePowerWords ? "Yes" : "No"}
    - Clickbait Style: ${includeClickbait ? "Yes (Keep it ethical but highly click-driven)" : "No (Focus on search intent and clarity)"}
    
    CRITICAL INSTRUCTION:
    Respond ONLY with a raw, valid JSON object. Do not include markdown blocks like \`\`\`json.
    
    JSON SCHEMA REQUIRED:
    {
      "primaryTitle": "The single best, highest-CTR title optimized for the chosen length.",
      "titleVariations": [
        "Provide 5 to 7 alternative titles...",
        "Each with a different emotional hook...",
        "Strictly matching the ${lengthPref} length requirement..."
      ],
      "bonusAngles": [
        "Alternative Angle 1: Focus on X",
        "Alternative Angle 2: Focus on Y",
        "Alternative Angle 3: Focus on Z"
      ],
      "thumbnailTexts": [
        "2-5 word highly readable thumbnail text",
        "Another short punchy text",
        "Curiosity gap text"
      ]
      ${includeDescription ? ',\n"description": "A high-retention, SEO-optimized YouTube video description (3-4 sentences). Incorporate keywords naturally, outline the value, and sound human."' : ''}
      ${includeSeoTags ? ',\n"seoTags": ["tag1", "long tail tag 2", "relevant keyword 3", "Generate exactly 15-20 highly searched relevant tags"]' : ''}
      ${includeHashtags ? ',\n"hashtags": ["#Topic", "#Trending", "Generate exactly 10-15 highly relevant YouTube hashtags (no spaces)"]' : ''}
    }`;
  };

  // Generate content (Credit deduction logic exactly as requested)
  const generateMetadata = async () => {
    if (!topic.trim()) {
      setError("Please provide a Video Topic.");
      return;
    }
    if (apiKeys.length === 0) {
      setError("No API keys available. Please try again later.");
      return;
    }
    if (credits < creditCost) {
      setError(`Insufficient credits. You need ${creditCost} credits to generate this content.`);
      return;
    }

    setGenerating(true);
    setError("");
    setResultData(null);

    try {
      // Deduct credits (Unchanged)
      const deductResponse = await axios.post(
        `https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/deduct-credit`,
        {
          userEmail: user.email,
          amount: creditCost,
        }
      );
      if (!deductResponse.data.success) {
        throw new Error("Failed to deduct credits");
      }
      setCredits((prev) => prev - creditCost);

      // Call AI
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.85,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 2500,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (data.candidates && data.candidates.length > 0 && data.candidates[0].content) {
        let generatedText = data.candidates[0].content.parts[0].text;
        
        // Clean markdown backticks to parse JSON safely
        generatedText = generatedText.replace(/```json/g, '').replace(/```/g, '').trim();
        
        try {
          const parsedContent = JSON.parse(generatedText);
          setResultData(parsedContent);
          toast.success("YouTube Metadata generated successfully!");
        } catch (parseErr) {
          throw new Error("AI returned invalid data format. Please try again.");
        }
      } else {
        throw new Error("No content was generated from the API response");
      }
    } catch (err) {
      console.error("Error generating content:", err);
      setError(`Error: ${err.message}`);
      toast.error(`Error: ${err.message}`);

      // Refund credits on failure (Unchanged)
      try {
        await axios.post(`https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/refund-credit`, {
          userEmail: user.email,
          amount: creditCost,
        });
        setCredits((prev) => prev + creditCost);
      } catch (refundErr) {
        console.error("Error refunding credits:", refundErr);
      }
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text, itemType) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${itemType} copied to clipboard!`);
    }).catch(() => {
      toast.error(`Failed to copy ${itemType}`);
    });
  };

  const downloadContent = () => {
    if (!resultData) return;
    
    let text = `YOUTUBE METADATA PACKAGE\nTopic: ${topic}\n\n`;
    
    text += `=== PRIMARY TITLE ===\n${resultData.primaryTitle}\n\n`;
    
    text += `=== TITLE VARIATIONS ===\n`;
    resultData.titleVariations.forEach(title => text += `- ${title}\n`);
    text += `\n`;

    if (resultData.description) {
      text += `=== VIDEO DESCRIPTION ===\n${resultData.description}\n\n`;
    }
    
    if (resultData.seoTags) {
      text += `=== SEO TAGS ===\n${resultData.seoTags.join(", ")}\n\n`;
    }
    
    if (resultData.hashtags) {
      text += `=== HASHTAGS ===\n${resultData.hashtags.join(" ")}\n\n`;
    }

    text += `=== BONUS ANGLES ===\n`;
    resultData.bonusAngles.forEach(angle => text += `- ${angle}\n`);
    text += `\n`;

    text += `=== THUMBNAIL TEXT IDEAS ===\n`;
    resultData.thumbnailTexts.forEach(txt => text += `- ${txt}\n`);
    text += `\n`;
    
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${topic.replace(/\s+/g, "-")}-yt-metadata.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Metadata package downloaded!");
  };

  if (loadingAuth || loading || loadingKeys || loadingTools) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
            <div style={{ border: '4px solid #f1f5f9', borderTop: '4px solid #FF0000', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite', margin: '0 auto 10px' }}></div>
            <p style={{ color: '#64748b', fontWeight: '500' }}>Loading Workspace...</p>
        </div>
      
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <div style={{ background: 'white', padding: '3rem', borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.08)', textAlign: 'center', maxWidth: '400px' }}>
          <Youtube size={48} color="#FF0000" style={{ marginBottom: '1rem' }} />
          <h2 style={{ color: '#0f172a', marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '700' }}>Authentication Required</h2>
          <button onClick={() => (window.location.href = "/login")} style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #FF0000 0%, #cc0000 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', width: '100%' }}>
            Proceed to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .yt-meta-app { font-family: 'Inter', system-ui, sans-serif; background: #f8fafc; min-height: 100vh; padding: 2rem 1rem; color: #334155; }
        .saas-card { background: #ffffff; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.04); border: 1px solid #e2e8f0; padding: 1.75rem; height: 100%; }
        .form-label { font-weight: 600; color: #475569; font-size: 0.9rem; margin-bottom: 0.5rem; display: block; }
        .form-control, .form-select { width: 100%; padding: 0.85rem 1rem; border-radius: 10px; border: 1px solid #cbd5e1; background: #f8fafc; font-size: 0.95rem; color: #0f172a; transition: all 0.2s; margin-bottom: 1.25rem; box-sizing: border-box; }
        .form-control:focus, .form-select:focus { outline: none; border-color: #FF0000; box-shadow: 0 0 0 3px rgba(255, 0, 0, 0.1); background: #fff; }
        
        .gradient-btn { background: linear-gradient(135deg, #FF0000 0%, #D22B2B 100%); color: white; border: none; padding: 1rem 1.5rem; border-radius: 10px; font-weight: 600; font-size: 1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 0.5rem; transition: all 0.3s ease; width: 100%; box-shadow: 0 4px 12px rgba(255, 0, 0, 0.2); }
        .gradient-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(255, 0, 0, 0.3); }
        .gradient-btn:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; transform: none; }
        
        .toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: .4s; border-radius: 34px; }
        .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; }
        input:checked + .slider { background-color: #10b981; }
        input:checked + .slider:before { transform: translateX(20px); }

        .result-section { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 1.5rem; margin-bottom: 1.5rem; position: relative; }
        .result-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; padding-bottom: 0.75rem; border-bottom: 1px solid #e2e8f0; }
        .result-title { font-size: 1.1rem; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 8px; margin: 0; }
        .copy-btn { background: white; border: 1px solid #cbd5e1; color: #64748b; padding: 6px 12px; border-radius: 6px; font-size: 0.8rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: all 0.2s; }
        .copy-btn:hover { background: #f1f5f9; color: #0f172a; }
        
        .tag-pill { display: inline-block; background: #e2e8f0; color: #334155; padding: 6px 12px; border-radius: 20px; font-size: 0.85rem; font-weight: 500; margin: 0 6px 8px 0; }
        .hashtag-pill { display: inline-block; color: #2563eb; font-weight: 600; margin: 0 10px 8px 0; font-size: 0.95rem; }
        
        @keyframes custom-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(0.98); } }
        .anim-pulse { animation: custom-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}</style>

      <div className="yt-meta-app">
       
        
        <div className="container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Top Navigation */}
          <div className="row mb-4">
            <div className="col-12 d-flex justify-content-between align-items-center" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => window.history.back()} style={{ background: "#fff", border: "1px solid #e2e8f0", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", color: "#64748b" }}>
                ← Dashboard
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", background: "white", padding: "8px 20px", borderRadius: "30px", border: "1px solid #e2e8f0", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                <span style={{ fontWeight: "600", color: "#64748b", fontSize: "0.9rem" }}>Credits:</span>
                <span style={{ color: credits < creditCost ? "#ef4444" : "#10b981", fontWeight: "800", fontSize: "1.1rem" }}>{credits}</span>
                {credits < creditCost && (
                  <button onClick={() => window.location.href = "/user/select-package"} style={{ background: "#10b981", color: "white", border: "none", padding: "6px 14px", borderRadius: "20px", cursor: "pointer", fontSize: "0.8rem", fontWeight: "700" }}>
                    Refill
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-5" style={{ textAlign: "center", marginBottom: "3rem" }}>
            <h1 style={{ fontSize: "2.5rem", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", letterSpacing: "-0.02em" }}>
              <Youtube color="#FF0000" size={40} /> Title & Tags Generator
            </h1>
            <p style={{ color: "#64748b", marginTop: "12px", fontSize: "1.1rem", maxWidth: "650px", margin: "12px auto 0" }}>
              Generate high-CTR titles, optimized SEO tags, and trending hashtags to skyrocket your discoverability.
            </p>
          </div>

          {error && (
            <div style={{ background: "#fef2f2", color: "#ef4444", padding: "1rem", borderRadius: "10px", marginBottom: "2rem", textAlign: "center", border: "1px solid #fecaca", fontWeight: "600" }}>
              {error}
            </div>
          )}

          <div className="row" style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
            
            {/* Left Column - Form */}
            <div className="col-lg-4 col-md-5" style={{ flex: '1 1 350px', maxWidth: '450px' }}>
              <div className="saas-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #f1f5f9' }}>
                  <Target size={20} color="#FF0000" />
                  <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a', fontWeight: '700' }}>Targeting Settings</h3>
                </div>
                
                <label className="form-label">Core Video Topic <span style={{color:'#ef4444'}}>*</span></label>
                <textarea
                  className="form-control"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., How to build a SaaS in 24 hours with AI..."
                  rows={2}
                />

                <label className="form-label">Target Audience</label>
                <select 
                  className="form-select" 
                  value={audience} 
                  onChange={(e) => setAudience(e.target.value)}
                >
                  {audienceOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Content Type</label>
                    <select className="form-select" value={contentType} onChange={(e) => setContentType(e.target.value)}>
                      {contentTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Tone</label>
                    <select className="form-select" value={tone} onChange={(e) => setTone(e.target.value)}>
                      {toneOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                </div>

                <label className="form-label">Title Length Preference</label>
                <select className="form-select" value={lengthPref} onChange={(e) => setLengthPref(e.target.value)}>
                  {lengthOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>

                <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '12px', marginTop: '0.5rem', marginBottom: '1.5rem', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#1e293b', display: 'block', fontSize: '0.9rem' }}>Power Words</span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Boost emotional CTR</span>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={includePowerWords} onChange={(e) => setIncludePowerWords(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#1e293b', display: 'block', fontSize: '0.9rem' }}>Clickbait Elements</span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Aggressive curiosity gaps</span>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={includeClickbait} onChange={(e) => setIncludeClickbait(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div style={{ borderTop: '1px solid #e2e8f0', margin: '0.5rem 0', paddingTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#1e293b', display: 'block', fontSize: '0.9rem' }}>Video Description</span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Generate SEO description</span>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={includeDescription} onChange={(e) => setIncludeDescription(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#1e293b', display: 'block', fontSize: '0.9rem' }}>SEO Tags</span>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={includeSeoTags} onChange={(e) => setIncludeSeoTags(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#1e293b', display: 'block', fontSize: '0.9rem' }}>Hashtags</span>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={includeHashtags} onChange={(e) => setIncludeHashtags(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>

                </div>

                <button onClick={generateMetadata} disabled={generating || !topic.trim() || credits < creditCost} className="gradient-btn">
                  {generating ? <Clock size={18} className="fa-spin" /> : <Sparkles size={18} />}
                  {generating ? "Optimizing..." : `Generate Metadata (${creditCost} Credits)`}
                </button>
              </div>
            </div>

            {/* Right Column - Results */}
            <div className="col-lg-8 col-md-7" style={{ flex: '1 1 500px' }}>
              
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                <h2 style={{ margin: 0, fontSize: "1.5rem", color: "#0f172a", fontWeight: "700" }}>Generated Results</h2>
                {resultData && (
                  <button onClick={downloadContent} style={{ background: "#10b981", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "0.9rem", fontWeight: "600", boxShadow: "0 2px 4px rgba(16, 185, 129, 0.2)" }}>
                    <Download size={16} /> Export All
                  </button>
                )}
              </div>

              {generating ? (
                 <div className="saas-card anim-pulse" style={{ textAlign: "center", padding: "6rem 2rem", display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: '70px', height: '70px', background: '#fee2e2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                      <TrendingUp size={34} color="#FF0000" />
                    </div>
                    <h3 style={{ color: '#0f172a', fontWeight: '800', marginBottom: '0.5rem' }}>Analyzing Search Intent...</h3>
                    <p style={{ color: "#64748b", maxWidth: "350px", margin: "0 auto" }}>Crafting high-CTR titles and discovering rankable tags for the algorithm.</p>
                 </div>
              ) : resultData ? (
                <div className="saas-card" style={{ padding: '2rem' }}>
                  
                  {/* Primary Title */}
                  <div className="result-section" style={{ background: '#fff', borderLeft: '4px solid #ef4444', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                    <div className="result-header">
                      <h3 className="result-title"><Type size={18} color="#ef4444" /> Best Performing Title</h3>
                      <button className="copy-btn" onClick={() => copyToClipboard(resultData.primaryTitle, "Primary Title")}>
                        <Copy size={14} /> Copy
                      </button>
                    </div>
                    <p style={{ fontSize: '1.4rem', fontWeight: '800', color: '#0f172a', margin: 0, lineHeight: '1.3' }}>
                      {resultData.primaryTitle}
                    </p>
                  </div>

                  {/* Title Variations */}
                  <div className="result-section">
                    <div className="result-header">
                      <h3 className="result-title"><LayoutTemplate size={18} color="#3b82f6" /> Title Variations</h3>
                      <button className="copy-btn" onClick={() => copyToClipboard(resultData.titleVariations.join("\n"), "Variations")}>
                        <Copy size={14} /> Copy All
                      </button>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#334155', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {resultData.titleVariations.map((variation, idx) => (
                        <li key={idx} style={{ fontSize: '1.05rem', fontWeight: '500' }}>{variation}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Description Section */}
                  {resultData.description && (
                    <div className="result-section">
                      <div className="result-header">
                        <h3 className="result-title"><FileText size={18} color="#0ea5e9" /> Video Description</h3>
                        <button className="copy-btn" onClick={() => copyToClipboard(resultData.description, "Description")}>
                          <Copy size={14} /> Copy
                        </button>
                      </div>
                      <p style={{ color: '#334155', lineHeight: '1.6', fontSize: '0.95rem' }}>{resultData.description}</p>
                    </div>
                  )}

                  {/* Tags & Hashtags Split Row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    
                    {/* SEO Tags */}
                    {resultData.seoTags && (
                      <div className="result-section" style={{ marginBottom: 0 }}>
                        <div className="result-header">
                          <h3 className="result-title"><TagIcon size={18} color="#10b981" /> SEO Tags</h3>
                          <button className="copy-btn" onClick={() => copyToClipboard(resultData.seoTags.join(", "), "SEO Tags")}>
                            <Copy size={14} /> Copy
                          </button>
                        </div>
                        <div>
                          {resultData.seoTags.map((tag, idx) => (
                            <span key={idx} className="tag-pill">{tag}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hashtags */}
                    {resultData.hashtags && (
                      <div className="result-section" style={{ marginBottom: 0 }}>
                        <div className="result-header">
                          <h3 className="result-title"><Hash size={18} color="#8b5cf6" /> Hashtags</h3>
                          <button className="copy-btn" onClick={() => copyToClipboard(resultData.hashtags.join(" "),"Hashtags")}>
                            <Copy size={14} /> Copy
                          </button>
                        </div>
                        <div>
                          {resultData.hashtags.map((hashtag, idx) => (
                            <span key={idx} className="hashtag-pill">{hashtag}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bonus Angles & Thumbnails */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    <div className="result-section" style={{ background: '#fffbeb', border: '1px solid #fde68a', marginBottom: 0 }}>
                      <h3 className="result-title" style={{ color: '#b45309', marginBottom: '1rem' }}><Sparkles size={18} /> Bonus Angles</h3>
                      <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#92400e', fontSize: '0.95rem', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {resultData.bonusAngles.map((angle, idx) => <li key={idx}>{angle}</li>)}
                      </ul>
                    </div>
                    
                    <div className="result-section" style={{ background: '#f8fafc', border: '1px solid #cbd5e1', marginBottom: 0 }}>
                      <h3 className="result-title" style={{ color: '#334155', marginBottom: '1rem' }}><ImageIcon size={18} /> Thumbnail Text Ideas</h3>
                      <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#475569', fontSize: '0.95rem', display: 'flex', flexDirection: 'column', gap: '6px', fontWeight: '700', textTransform: 'uppercase' }}>
                        {resultData.thumbnailTexts.map((txt, idx) => <li key={idx}>{txt}</li>)}
                      </ul>
                    </div>
                  </div>

                </div>
              ) : (
                <div className="saas-card" style={{ textAlign: 'center', padding: '6rem 2rem', border: '2px dashed #cbd5e1', background: 'transparent', boxShadow: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <TrendingUp size={56} color="#cbd5e1" style={{ marginBottom: '1.5rem' }} />
                  <h3 style={{ color: '#64748b', fontWeight: '700', margin: '0 0 0.5rem 0' }}>Ready to Optimize</h3>
                  <p style={{ color: '#94a3b8', margin: 0, maxWidth: '350px' }}>Enter your video topic to generate titles, tags, and hashtags designed for the algorithm.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default YoutubeTitleTagsGenerator;